import { useState } from 'react';
import { ErrorsMap, UseValidatorArgs, CheckErrors, ErrorExpression } from './useValidator.types';
import { makeEmptyErrorsMap } from './useValidator.utils';

export const useValidator = <Errors extends ErrorsMap>({
  errorExpressions,
  inputNames,
}: UseValidatorArgs): {
  errors: Errors;
  checkErrors: CheckErrors;
} => {
  const [errors, setErrors] = useState<Errors>(
    makeEmptyErrorsMap<Errors>(inputNames),
  );
  
  const isIterable = (value: ErrorExpression[]) => {
    return Boolean(value) && typeof value[Symbol.iterator] === 'function';
  }
  
  const validateErrorValue = (value: string, errorExpressions: ErrorExpression[], context: unknown) => {
    for (const errorExpression of errorExpressions) {
      const { format, errorMessage } = errorExpression;
      const isValid = typeof format === 'function'
        ? format(value, context)
        : format.test(value);
      
      if (!isValid) {
        return errorMessage;
      }
    }
    return '';
  }
  
  const checkErrors: CheckErrors = (values, context, scroll) => {
    const newErrors: ErrorsMap = errors;
    let hasErrors = false;
    
    for (const name in values) {
      if (!isIterable(errorExpressions[name])) {
        continue;
      }
      const errorMessage = validateErrorValue(values[name], errorExpressions[name], context && context[name]);
      if (errorMessage) {
        newErrors[name] = errorMessage;
        hasErrors = true;
      } else {
        newErrors[name] = '';
      }
    }
    
    setErrors(Object.assign({}, newErrors) as Errors);
    if (scroll) {
      let firstElemIndex = 10000;
      let firstInput = null;
      
      [errors, newErrors].forEach((errors) => {
        for (const key in errors) {
          if (errors[key]) {
            const elem = document.getElementById(key);
            if (elem) {
              const { order } = elem.dataset;
              if (order && +order < firstElemIndex) {
                firstElemIndex = +order;
                firstInput = elem;
              }
            }
          }
        }
      });
      
      if (firstElemIndex !== 10000) {
        const prevElement = document.querySelector(
          `[data-order='${firstElemIndex}']`,
        );
        if (prevElement && typeof prevElement.scrollIntoView === 'function') {
          prevElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    }
    return hasErrors;
  };
  return { errors, checkErrors };
};
